/**
 * Typing a date and a time, rather than operating a picker.
 *
 * The native `<input type="date">` and `<input type="time">` were doing this
 * job and doing it badly: each one hangs a browser-drawn button off its right
 * edge, over the very characters the field exists to show, and neither can be
 * told that this app only plans three weeks of September. What follows is the
 * arithmetic behind two ordinary text fields with a list of suggestions under
 * them — the Google Calendar arrangement, where typing is the fast path and
 * the list is there to finish the thought.
 */

/**
 * The only dates this app plans: Climate Week, plus the week either side.
 *
 * Anything outside it is a typo rather than a plan. Bounding the field is what
 * lets it be forgiving everywhere else — "23" can mean one thing when there
 * are twenty-one candidates and nothing at all when there are thirty thousand.
 */
export const FIRST_DAY = '2026-09-13';
export const LAST_DAY = '2026-10-03';

/** Suggestions land on the quarter-hour. Finer would be a list nobody reads. */
export const TIME_STEP_MINUTES = 15;

export interface DayOption {
  /** ISO day key, as everything else in the app stores it. */
  key: string;
  /** "Wednesday, September 23" */
  full: string;
  /** "Wed, Sept 23" */
  medium: string;
  /** "Wed, 9/23" */
  short: string;
  /** The number in the calendar cell. */
  dayOfMonth: number;
  /** 1-12, for "9/26" and friends. */
  month: number;
  /** The words this day answers to, lowercased: weekday and month, long and short. */
  words: string[];
}

const WEEKDAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
/** September is "Sept" rather than "Sep": it is the month this app is about. */
const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec',
];

const asDate = (key: string) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

const asKey = (date: Date) =>
  `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(
    date.getUTCDate(),
  ).padStart(2, '0')}`;

/**
 * How one day is written, in the three lengths the field can show.
 *
 * Works for any date, not only the selectable ones: a draft can arrive holding
 * a day outside the range — from a click on a pre-week day already on the
 * schedule — and a field that showed nothing at all would look broken. The
 * range binds what can be *chosen*, not what can be displayed.
 */
export function describeDay(key: string): DayOption | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return null;
  const date = asDate(key);
  if (Number.isNaN(date.getTime())) return null;
  const weekday = WEEKDAY[date.getUTCDay()];
  const month = date.getUTCMonth();
  const day = date.getUTCDate();

  const full = `${weekday}, ${MONTH[month]} ${day}`;
  const medium = `${weekday.slice(0, 3)}, ${MONTH_SHORT[month]} ${day}`;
  const short = `${weekday.slice(0, 3)}, ${month + 1}/${day}`;

  return {
    key,
    full,
    medium,
    short,
    dayOfMonth: day,
    month: month + 1,
    words: [weekday, weekday.slice(0, 3), MONTH[month], MONTH_SHORT[month]].map((w) =>
      w.toLowerCase(),
    ),
  };
}

/** Every date the form will accept, in order. */
export const SELECTABLE_DAYS: DayOption[] = (() => {
  const out: DayOption[] = [];
  const last = asDate(LAST_DAY).getTime();
  for (let d = asDate(FIRST_DAY); d.getTime() <= last; d.setUTCDate(d.getUTCDate() + 1)) {
    const day = describeDay(asKey(d));
    if (day) out.push(day);
  }
  return out;
})();

export const dayOption = (key: string): DayOption | null =>
  SELECTABLE_DAYS.find((d) => d.key === key) ?? describeDay(key);

/**
 * The shape of the calendar the dates are chosen from.
 *
 * Seven columns, Sunday first, with however many empty cells it takes to put
 * the first date in its own column. The range as it stands is exactly three
 * full weeks beginning on a Sunday, so there are none — but the grid is built
 * from the dates rather than from that coincidence, which would otherwise go
 * silently wrong the first time the range moved by a day.
 */
export const CALENDAR_COLUMNS = 7;
export const LEADING_BLANKS = asDate(FIRST_DAY).getUTCDay();
export const TRAILING_BLANKS =
  (CALENDAR_COLUMNS - ((LEADING_BLANKS + SELECTABLE_DAYS.length) % CALENDAR_COLUMNS)) %
  CALENDAR_COLUMNS;

/** Sunday-first initials for the column heads. */
export const COLUMN_HEADS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/** "September – October 2026": whatever months the range actually covers. */
export const CALENDAR_TITLE = (() => {
  const first = asDate(FIRST_DAY);
  const last = asDate(LAST_DAY);
  const year = last.getUTCFullYear();
  const from = MONTH[first.getUTCMonth()];
  const to = MONTH[last.getUTCMonth()];
  return from === to ? `${from} ${year}` : `${from} – ${to} ${year}`;
})();

export const inRange = (key: string): boolean => key >= FIRST_DAY && key <= LAST_DAY;

/**
 * Whether one word of a query describes this day.
 *
 * Each token is matched against the *part* it could be, rather than against
 * one blob of every spelling joined together. That blob is what made
 * "Saturday, September 26" resolve to Saturday the 19th: every day's ISO key
 * carries the year, "2026" contains "26", and so the day number matched all
 * twenty-one days. The first Saturday won, and the date silently moved a week
 * every time the field was touched.
 */
type Match = 'exact' | 'partial' | false;

function tokenMatches(day: DayOption, token: string): Match {
  // "9/26", "09-26", "9.26" — a month and a day, in that order.
  const monthDay = /^(\d{1,2})[/.-](\d{1,2})$/.exec(token);
  if (monthDay) {
    if (Number(monthDay[1]) !== day.month) return false;
    if (Number(monthDay[2]) === day.dayOfMonth) return 'exact';
    // Half-typed: "9/2" on the way to "9/23" still offers the twenties.
    return String(day.dayOfMonth).startsWith(monthDay[2]) ? 'partial' : false;
  }

  // The whole ISO key, as the stored value is written.
  if (/^\d{4}-\d{2}-\d{2}$/.test(token)) return token === day.key ? 'exact' : false;

  // A bare number is a day of the month. A prefix still counts, so "2" offers
  // the twenties to someone in the middle of typing "23" — but only as second
  // best, or "Thursday 1" would land on Thursday the 17th.
  if (/^\d{1,2}$/.test(token)) {
    if (Number(token) === day.dayOfMonth) return 'exact';
    return String(day.dayOfMonth).startsWith(token) ? 'partial' : false;
  }

  // Anything else is a word: a weekday or a month, matched from the start so
  // "sep", "sept" and "september" all mean the same month.
  return day.words.some((word) => word.startsWith(token)) ? 'exact' : false;
}

/**
 * Days matching what has been typed so far.
 *
 * Every token has to describe the day, so "wed 23" and "23 wed" both work and
 * "sep 2" narrows rather than guesses. An empty query offers the lot: the list
 * doubles as the picker.
 */
export function matchDays(query: string): DayOption[] {
  const tokens = query.toLowerCase().split(/[\s,]+/).filter(Boolean);
  if (tokens.length === 0) return SELECTABLE_DAYS;

  const hits: { day: DayOption; exact: boolean }[] = [];
  for (const day of SELECTABLE_DAYS) {
    const marks = tokens.map((t) => tokenMatches(day, t));
    if (marks.some((m) => m === false)) continue;
    hits.push({ day, exact: marks.every((m) => m === 'exact') });
  }

  // A day the query describes exactly comes first; everything still matching
  // follows in date order, because the list is also the month on screen.
  return [...hits.filter((h) => h.exact), ...hits.filter((h) => !h.exact)].map((h) => h.day);
}

/** Every quarter-hour of the day, as minutes after midnight. */
export function timeOptions(from = 0, until = 24 * 60): number[] {
  const out: number[] = [];
  const first = Math.ceil(from / TIME_STEP_MINUTES) * TIME_STEP_MINUTES;
  for (let m = first; m <= until; m += TIME_STEP_MINUTES) out.push(m);
  return out;
}

/** "8:15 PM", the same shape the rest of the app writes times in. */
export function formatClock(minutes: number): string {
  const h24 = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  const suffix = h24 < 12 ? 'AM' : 'PM';
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h}:${String(m).padStart(2, '0')} ${suffix}`;
}

/** "1 hr 30 mins", for the duration a chosen end time would give. */
export function formatLength(minutes: number): string {
  if (minutes <= 0) return '0 mins';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const hours = h === 1 ? '1 hr' : h > 1 ? `${h} hrs` : '';
  const mins = m === 1 ? '1 min' : m > 0 ? `${m} mins` : '';
  return [hours, mins].filter(Boolean).join(' ');
}

/**
 * Reads a typed time as generously as it can.
 *
 * "8", "8p", "8:15pm", "815pm", "2015" and "20:15" all resolve. A bare hour
 * with no meridiem takes the reading a person means on a conference schedule:
 * 1 through 7 are afternoon, 8 through 12 morning — nobody typing "6" into an
 * evening plan means dawn.
 */
export function parseTime(text: string): number | null {
  const raw = text.trim().toLowerCase().replace(/\./g, '');
  if (!raw) return null;

  const match = /^(\d{1,2})(?::?(\d{2}))?\s*(am|pm|a|p)?$/.exec(raw);
  if (!match) return null;

  // The hour group is greedy but backs off, so "815" reads as 8:15 and "2015"
  // as 20:15 without any digit counting here.
  let hours = Number(match[1]);
  const minutes = match[2] == null ? 0 : Number(match[2]);
  if (minutes > 59) return null;

  const meridiem = match[3]?.[0];
  if (meridiem === 'a') {
    if (hours > 12) return null;
    if (hours === 12) hours = 0;
  } else if (meridiem === 'p') {
    if (hours > 12) return null;
    if (hours !== 12) hours += 12;
  } else if (hours >= 1 && hours <= 7) {
    // No meridiem: the afternoon reading. Nobody typing "6" into an evening
    // plan means dawn, and nobody schedules against 1am at all.
    hours += 12;
  } else if (hours > 23) {
    return null;
  }

  const total = hours * 60 + minutes;
  return total >= 0 && total < 24 * 60 ? total : null;
}

/**
 * Which of the offered times match what has been typed.
 *
 * Anything that parses cleanly snaps the list to that hour, so typing "8" or
 * "8p" leaves the four quarters of eight o'clock to choose between rather than
 * every time containing an 8.
 */
export function matchTimes(query: string, options: number[]): number[] {
  const raw = query.trim().toLowerCase();
  if (!raw) return options;

  const parsed = parseTime(raw);
  if (parsed != null) {
    const hour = Math.floor(parsed / 60);
    const sameHour = options.filter((m) => Math.floor(m / 60) === hour);
    if (sameHour.length > 0) return sameHour;
  }

  const needle = raw.replace(/\s+/g, '');
  const loose = options.filter((m) => formatClock(m).toLowerCase().replace(/\s+/g, '').includes(needle));
  return loose;
}
